import { Fragment, useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type RefObject } from 'react'
import { Link } from 'react-router'
import type { AppEntry } from '../api'
import { fileKey, formatDate, isCli, joinAnd, kindLabel, mainFile, osShort, publishedOs, requirement, sortForOs, useApps, type AppsView } from '../apps'
import { AppsAlert, AppsSkeleton, CliBadge, DownloadIcon, MineBadge, OsIcon, sizeOf } from './apps-ui'

/**
 * "Get the app" in the header (docs/apps-downloads.md 3.3, 3.7; artboards
 * Apps-1-HeaderMenu, Apps-4-Mobile). Hidden while the list loads and when
 * nothing is published; a failed load shows the button so the error can be seen.
 * Computers get a menu of the published files; phones a sheet with the link.
 */
export function AppsButton() {
  const apps = useApps()
  const [open, setOpen] = useState(false)
  const btn = useRef<HTMLButtonElement>(null)
  const close = useCallback((focus = true) => {
    setOpen(false)
    if (focus) btn.current?.focus()
  }, [])

  if (apps.state === 'loading' || apps.state === 'none') return null
  const phone = apps.os === 'phone'
  const popupId = phone ? 'apps-sheet' : 'apps-menu'

  return (
    <span className="relative flex">
      <button
        ref={btn}
        type="button"
        data-testid="apps-button"
        aria-label="Get the app"
        aria-haspopup={phone ? 'dialog' : 'menu'}
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!phone && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-md border text-sm font-medium sm:min-h-9 sm:px-3 ${
          open ? 'border-accent text-text' : 'border-line-2 text-text-soft hover:text-text'
        }`}
      >
        <DownloadIcon />
        <span className="hidden sm:inline">Get the app</span>
      </button>
      {open && (phone ? <AppsSheet apps={apps} onClose={close} /> : <AppsMenu apps={apps} btn={btn} onClose={close} />)}
    </span>
  )
}

const items = (root: HTMLElement | null) => Array.from(root?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])

function AppsMenu({ apps, btn, onClose }: { apps: AppsView; btn: RefObject<HTMLButtonElement | null>; onClose: (focus?: boolean) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const busy = apps.state === 'error' && apps.fetching
  const ready = apps.state === 'ready'

  // Focus the first item (or Try again) when the menu opens and after a reload.
  useEffect(() => {
    const first = items(ref.current)[0] ?? ref.current?.querySelector<HTMLElement>('button')
    first?.focus()
  }, [apps.state, busy])

  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || btn.current?.contains(t)) return
      onClose(false)
    }
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [btn, onClose])

  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      return
    }
    if (e.key === 'Tab') {
      onClose(false)
      return
    }
    const list = items(ref.current)
    if (!list.length) return
    const i = list.indexOf(document.activeElement as HTMLElement)
    const go = (n: number) => {
      e.preventDefault()
      list[(n + list.length) % list.length]?.focus()
    }
    if (e.key === 'ArrowDown') go(i + 1)
    else if (e.key === 'ArrowUp') go(i < 0 ? list.length - 1 : i - 1)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(list.length - 1)
  }

  const m = apps.manifest
  return (
    <div
      ref={ref}
      id="apps-menu"
      data-testid="apps-menu"
      role={ready ? 'menu' : 'dialog'}
      aria-label="Download apps"
      onKeyDown={onKey}
      className="absolute right-0 top-[calc(100%+8px)] z-30 flex w-[500px] max-w-[calc(100vw-32px)] flex-col gap-0.5 rounded-lg border border-accent bg-panel p-2 shadow-pop"
    >
      {busy ? (
        <AppsSkeleton />
      ) : apps.state === 'error' ? (
        <div className="p-1">
          <AppsAlert onRetry={apps.retry} />
        </div>
      ) : (
        <>
          {apps.apps.map((app, n) => (
            <Fragment key={app.id}>
              {n > 0 && <div role="separator" className="mx-1 my-1.5 h-px bg-line" />}
              <MenuGroup app={app} apps={apps} onPick={() => onClose(false)} />
            </Fragment>
          ))}
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 pb-1 pt-2.5">
            {m && (
              <span className="font-mono text-[11px] text-faint">
                v{m.version}
                {m.released_at && ` · ${formatDate(m.released_at)}`}
              </span>
            )}
            <Link role="menuitem" to="/apps" onClick={() => onClose(false)} className="rounded-md text-[13px] text-accent no-underline hover:text-text">
              All downloads and checksums →
            </Link>
          </div>
        </>
      )}
    </div>
  )
}

function MenuGroup({ app, apps, onPick }: { app: AppEntry; apps: AppsView; onPick: () => void }) {
  const head = app.tagline ? `${app.name} · ${app.tagline}` : app.name
  return (
    <div role="group" aria-label={head} className="flex flex-col gap-0.5">
      <div aria-hidden className="mono-label px-3 pb-2 pt-1.5 uppercase">
        {head}
      </div>
      {sortForOs(app.files, apps.os).map((f) => {
        const mine = f === mainFile(app, apps.os)
        return (
          <a
            key={f.url}
            role="menuitem"
            href={f.url}
            download
            data-testid={`apps-item-${fileKey(app, f)}`}
            data-mine={mine}
            onClick={onPick}
            className={`flex items-center gap-3 rounded-md border px-3 py-2.5 text-text no-underline ${
              mine ? 'border-live/[.45] bg-live/[.06]' : 'border-transparent hover:bg-line/40'
            }`}
          >
            <span className="grid h-9 w-9 flex-none place-items-center rounded-md border border-line-2 text-text-soft">
              <OsIcon os={f.os} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-px">
              <span className="flex flex-wrap items-center gap-x-2 font-semibold">
                <span>
                  {app.name} for {osShort(f.os)}
                </span>
                {isCli(f) && <CliBadge testId={`apps-cli-badge-${fileKey(app, f)}`} />}
                {mine && <MineBadge />}
              </span>
              <span className="truncate text-xs text-muted">{requirement(f)}</span>
            </span>
            <span className="flex flex-none items-center gap-1.5 font-mono text-xs text-text-soft">
              {kindLabel(f)} · {sizeOf(f)}
              <DownloadIcon />
            </span>
          </a>
        )
      })}
    </div>
  )
}

/** Phone: the files are for a computer, so the sheet sends the link there. */
function AppsSheet({ apps, onClose }: { apps: AppsView; onClose: (focus?: boolean) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const closeBtn = useRef<HTMLButtonElement>(null)
  const [copied, setCopied] = useState(false)
  const busy = apps.state === 'error' && apps.fetching

  useEffect(() => {
    closeBtn.current?.focus()
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', key)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', key)
      document.body.style.overflow = overflow
    }
  }, [onClose])

  // Keep Tab inside the sheet.
  const trap = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Tab') return
    const f = Array.from(ref.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])
    if (!f.length) return
    const first = f[0]
    const last = f[f.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last?.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first?.focus()
    }
  }

  const names = apps.apps.map((a) => a.name)
  const oses = publishedOs(apps.apps).map(osShort)
  return (
    <div className="fixed inset-0 z-40">
      <div aria-hidden className="absolute inset-0 bg-bg/60" onClick={() => onClose()} />
      <div
        ref={ref}
        id="apps-sheet"
        data-testid="apps-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apps-sheet-title"
        onKeyDown={trap}
        className="absolute inset-x-0 bottom-0 flex flex-col gap-3 rounded-t-lg border-t border-accent bg-panel p-4 pb-[max(16px,env(safe-area-inset-bottom))] shadow-pop"
      >
        <span aria-hidden className="h-1 w-10 self-center rounded-md bg-line-2" />
        <div className="flex items-center justify-between gap-3">
          <h2 id="apps-sheet-title" className="m-0 text-lg font-semibold">
            Apps for your computer
          </h2>
          <button
            ref={closeBtn}
            type="button"
            aria-label="Close"
            onClick={() => onClose()}
            className="grid h-[44px] w-[44px] flex-none place-items-center rounded-md border border-line-2 text-muted"
          >
            ✕
          </button>
        </div>
        {busy ? (
          <AppsSkeleton rows={2} />
        ) : apps.state === 'error' ? (
          <AppsAlert onRetry={apps.retry} />
        ) : (
          <p className="m-0 text-sm text-text-soft">
            {joinAnd(names)} {names.length > 1 ? 'run' : 'runs'} on {joinAnd(oses)}. Open this page on your computer, or send yourself the link.
          </p>
        )}
        <button
          type="button"
          data-testid="apps-copy-link"
          onClick={async () => {
            await navigator.clipboard?.writeText(`${location.origin}/apps`).catch(() => undefined)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
          }}
          className="flex min-h-[44px] items-center justify-center rounded-md border border-live bg-live/[.12] font-semibold text-live"
        >
          {copied ? '✓ Link copied' : 'Copy link to downloads'}
        </button>
        <span role="status" className="sr-only">
          {copied ? 'Link copied' : ''}
        </span>
        <Link to="/apps" onClick={() => onClose(false)} className="flex min-h-[44px] items-center justify-center text-sm text-accent no-underline">
          See all downloads →
        </Link>
      </div>
    </div>
  )
}
